import api from '@/shared/api';
import type { ApiResponse, BulkDeleteResult, Pagination, SortOrder } from '@/shared/lib';
import type {
  RecordHomologationPayload,
  RecordHomologationResponse,
  RecordPreviousRevisionResponse,
  RecordResponse,
} from '../model/records.types';
import type {
  RecordSearchType,
  RecordSortField,
  RecordStatusFilter,
} from '../lib/record-list-query';

export type { RecordResponse } from '../model/records.types';

export interface ListRecordsParams {
  page: number;
  itemsPerPage: number;
  term?: string;
  searchType?: RecordSearchType;
  sort?: RecordSortField;
  order?: SortOrder;
  status?: RecordStatusFilter;
  generatedRecords?: string;
}

interface PaginatedEnvelope<T> {
  data: T[];
  metadata: Pagination;
}

/**
 * The newest revision of every record code, which is what the listing shows as a
 * row. The previous revisions are not in this response: they are loaded on
 * demand by `listRecordPreviousRevisions` when the arrow is clicked.
 */
export async function listRecords(
  params: ListRecordsParams
): Promise<PaginatedEnvelope<RecordResponse>> {
  const response = await api.get<PaginatedEnvelope<RecordResponse>>('/matrices/latest-revisions', {
    params,
  });

  return response.data;
}

/**
 * Previous revisions of one record, from the newest to the oldest.
 *
 * `id` is the id of the row the arrow belongs to, which is the newest revision:
 * the endpoint reads its code and its revision number and answers with what sits
 * behind it.
 */
export async function listRecordPreviousRevisions(
  id: number
): Promise<RecordPreviousRevisionResponse[]> {
  const response = await api.get<ApiResponse<RecordPreviousRevisionResponse[]>>(
    `/matrices/${id}/previous-revisions`
  );

  return response.data.data ?? [];
}

export { getRecordControl, listRecordChangelog } from '@/widgets/record-modals';

export async function deleteRecord(id: number): Promise<void> {
  await api.delete(`/matrices/${id}`);
}

export async function deleteRejectedRecord(id: number): Promise<void> {
  await api.delete(`/matrices/${id}`, { params: { rejectedOnly: true } });
}

export async function deleteRecords(ids: number[]): Promise<{ data: BulkDeleteResult }> {
  const response = await api.delete<ApiResponse<BulkDeleteResult>>('/matrices/bulk', {
    data: { ids },
  });

  return { data: response.data.data! };
}

export async function updateRecordStatus(id: number, isActive: boolean): Promise<void> {
  await api.patch(`/matrices/${id}/status`, { isActive });
}

/** `id` is the matrix id, so an expanded row reads its own revision. */
export async function getRecordHomologation(id: number): Promise<RecordHomologationResponse> {
  const response = await api.get<ApiResponse<RecordHomologationResponse>>(
    `/matrices/${id}/homologation`
  );

  return response.data.data!;
}

/** Answers with what the database now holds, not with what the form hoped to write. */
export async function updateRecordHomologation(
  id: number,
  payload: RecordHomologationPayload
): Promise<RecordHomologationResponse> {
  const response = await api.patch<ApiResponse<RecordHomologationResponse>>(
    `/matrices/${id}/homologation`,
    stripUndefined(payload)
  );

  return response.data.data!;
}

/** Flat by design: the payload has no nested objects. */
function stripUndefined(payload: RecordHomologationPayload): Record<string, unknown> {
  return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));
}
